import {
  DATA_TABLE_METERBAR_MIN_COLUMN_WIDTH,
  DATA_TABLE_METERBAR_WITH_VALUE_MIN_COLUMN_WIDTH,
} from '../../constants.js';
import type {
  DataTableDisplayColumnDef,
  DataTableRowData,
} from '../../public.api.js';
import { isDatatableMeterBarColumnConfig } from '../ColumnTypes/column-types-types.js';

/**
 * Determines the minimum width for a column.
 */
export function getColumnMinWidth(
  columnDef: DataTableDisplayColumnDef<DataTableRowData, unknown>,
): number | undefined {
  const isMeterbar = columnDef.columnType === 'meterbar';
  const meterbarConfig = isDatatableMeterBarColumnConfig(
    columnDef.config,
    columnDef.columnType,
  )
    ? columnDef.config
    : undefined;
  const meterbarShowsValue =
    meterbarConfig?.showValue === 'left' ||
    meterbarConfig?.showValue === 'right';

  let defaultMinWidth: number | undefined;
  if (meterbarShowsValue) {
    defaultMinWidth = DATA_TABLE_METERBAR_WITH_VALUE_MIN_COLUMN_WIDTH;
  } else if (isMeterbar) {
    defaultMinWidth = DATA_TABLE_METERBAR_MIN_COLUMN_WIDTH;
  }

  return Math.max(columnDef.minWidth ?? 0, defaultMinWidth ?? 0) || undefined;
}

/**
 * Typeguard function to make sure a passed width is a fraction width.
 * @internal
 */
export function isFractionWidth(
  width?:
    | number
    | `${number}fr`
    | 'auto'
    | 'content'
    | {
        type: 'auto' | 'content';
        maxWidth?: number;
      },
): width is `${number}fr` {
  return typeof width === 'string' && width.endsWith('fr');
}

/**
 * Typeguard function to make sure a passed width is an extended config.
 * @internal
 */
export function isWidthExtendedConfig(
  width?:
    | number
    | `${number}fr`
    | 'auto'
    | 'content'
    | {
        type: 'auto' | 'content';
        maxWidth?: number;
      },
): width is {
  type: 'auto' | 'content';
  maxWidth?: number;
} {
  return typeof width === 'object';
}
